import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import CustomSelect, { MenuData } from "../../../components/input/CustomSelect";
import { common, financedEmissions } from "../../../i18n";
import { ListingStatus } from "../../../models/dto/cat15/cat15.types";
import { getSectors } from "../../../service/cat15.service";
import SectorIsicPicker from "../../../components/cat15/SectorIsicPicker";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit?: (values: AddBorrowerForm) => void;
}

export interface AddBorrowerForm {
  taxId: string;
  nameEN: string;
  nameTH: string;
  /** ISIC code — a BOT industry code, or a section that has none. PRIMARY sector (required). */
  sectorIsic: string;
  /** TSIC section code — optional secondary classification. */
  sectorTsic: string;
  /** "" = not provided: the borrower is asked when they sign in. */
  listingStatus: ListingStatus | "";
}

// Mask a Thai tax ID as the user types: 13 digits grouped 1-4-5-2-1 with
// dashes, e.g. "0-1055-59999-99-9" (matches the placeholder hint).
const formatThaiTaxId = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 13);
  return [d.slice(0, 1), d.slice(1, 5), d.slice(5, 10), d.slice(10, 12), d.slice(12, 13)]
    .filter((part) => part.length > 0)
    .join("-");
};

const toMenuData = (s: { value: string; labelEn: string; labelTh: string }): MenuData => ({
  value: s.value,
  nameEN: s.labelEn,
  nameTH: s.labelTh,
});

const AddBorrowerModal = ({ open, onClose, onSubmit }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const [tsicOptions, setTsicOptions] = useState<MenuData[]>([]);
  const [isicError, setIsicError] = useState(false);

  // TSIC (optional) sections load when the modal opens; the ISIC picker loads its own.
  useEffect(() => {
    if (!open) return;
    let active = true;
    getSectors("tsic").then((s) => active && setTsicOptions(s.map(toMenuData)));
    return () => {
      active = false;
    };
  }, [open]);

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<AddBorrowerForm>({
      defaultValues: {
        taxId: "",
        nameEN: "",
        nameTH: "",
        sectorIsic: "",
        sectorTsic: "",
        listingStatus: "",
      },
    });

  const sectorIsic = watch("sectorIsic");
  const sectorTsic = watch("sectorTsic");
  const listingStatus = watch("listingStatus");

  const submit = (values: AddBorrowerForm) => {
    // ISIC is the primary sector — block submit when it's empty.
    if (!values.sectorIsic) {
      setIsicError(true);
      return;
    }
    onSubmit?.(values);
    reset();
    setIsicError(false);
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="560px"
      header={<Typography variant="h1">+ {t("Add-Borrower")}</Typography>}
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          <Box>
            <InputReactHookForm
              required
              name="taxId"
              control={control}
              label={t("Tax-ID")}
              placeholder="0-1055-59999-99-9"
              maxLength={17}
              formatValue={formatThaiTaxId}
              rules={{ required: t("Tax-ID") }}
            />
            <Typography variant="body2" color="var(--gray1)" mt="4px">
              {t("Tax-ID-Hint")}
            </Typography>
          </Box>
          <Grid container spacing="20px">
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                required
                name="nameEN"
                control={control}
                label={t("Name-EN")}
                placeholder="Company name"
                rules={{ required: t("Name-EN") }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                name="nameTH"
                control={control}
                label={t("Name-TH")}
                placeholder="ชื่อบริษัท"
              />
            </Grid>
          </Grid>
          {/* PRIMARY sector = ISIC (required); TSIC is an optional secondary classification. */}
          <SectorIsicPicker
            key={open ? "open" : "closed"}
            name="sectorIsic"
            control={control}
            value={sectorIsic}
            onChange={(code) => {
              setValue("sectorIsic", code);
              setIsicError(false);
            }}
            error={isicError}
            errorMessage={t("Sector-ISIC-Required")}
            unavailableMessage={t("Sector-ISIC-Unavailable")}
          />
          <CustomSelect
            label={t("Sector-TSIC")}
            placeholder={t("Sector-Select-Optional")}
            value={sectorTsic}
            listMenuItem={tsicOptions}
            handleValeOnChange={(e) => setValue("sectorTsic", e.target.value)}
            resetValue={() => setValue("sectorTsic", "")}
          />
          <Box>
            <Typography variant="body2" color="var(--gray9)" mb="4px">
              {t("Listing-Status")}
            </Typography>
            <RadioGroup
              row
              value={listingStatus}
              onChange={(e) =>
                setValue("listingStatus", e.target.value as ListingStatus | "")
              }
            >
              <FormControlLabel
                value="listed"
                control={<Radio />}
                label={t("Listed")}
              />
              <FormControlLabel
                value="unlisted"
                control={<Radio />}
                label={t("Unlisted")}
              />
              <FormControlLabel
                value=""
                control={<Radio />}
                label={t("Not-Provided")}
              />
            </RadioGroup>
            {listingStatus === "" && (
              <Typography variant="body2" color="var(--gray9)">
                {t("Listing-Not-Provided-Helper")}
              </Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="flex-end" gap="12px">
            <Button variant="outlined" color="secondary" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit(submit)}
            >
              {t("Create-Borrower")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default AddBorrowerModal;
